import { cn } from "@/lib/utils/index";
import { FETCH_MODAL_RING_ROOM_CLASS } from "./constants";

/** Width preset of a `FetchModal`. */
export type FetchModalSize = "auto" | "xs" | "sm" | "md" | "lg" | "xl" | "full";

/**
 * Height behavior of a `FetchModal` preset size.
 * - `"fit"` hugs the content, up to the preset's max-height cap, then scrolls inside.
 * - `"fill"` always stretches to the cap, for tabbed or async content that would otherwise
 *   resize while the modal is open, and for children that fill their parent with `h-full`.
 */
export type FetchModalHeight = "fit" | "fill";

/** Classes and inline cap a size preset resolves to. */
export interface ModalSizeClasses {
  widthClasses: string;
  heightClasses: string;
  maxHeight: string;
}

interface PresetSize {
  widthClasses: string;
  maxHeightClass: string;
  maxHeight: string;
}

// Class names are written out in full so Tailwind's scanner sees them.
const PRESET_SIZES: Record<Exclude<FetchModalSize, "auto" | "full">, PresetSize> = {
  xs: { widthClasses: "w-full max-w-md", maxHeightClass: "max-h-[80vh]", maxHeight: "80vh" },
  sm: { widthClasses: "w-full max-w-2xl", maxHeightClass: "max-h-[80vh]", maxHeight: "80vh" },
  md: { widthClasses: "w-full max-w-4xl", maxHeightClass: "max-h-[85vh]", maxHeight: "85vh" },
  lg: { widthClasses: "w-full max-w-6xl", maxHeightClass: "max-h-[90vh]", maxHeight: "90vh" },
  xl: { widthClasses: "w-full max-w-7xl", maxHeightClass: "max-h-[90vh]", maxHeight: "90vh" },
};

// Frame shared by every non-full-screen panel. Radius and padding are one tier (24px each), the
// same as the admin command palette; the hairline border and soft neutral shadow match it too.
const PANEL_FRAME_CLASS = "rounded-3xl relative my-8";
const PANEL_SURFACE_CLASS = "border border-gray-200 shadow-lg shadow-gray-900/[0.08]";

// Mobile sheet frame. Applied before the caller's `className`, so a caller that already shaped
// its mobile layout (its own height, radius or padding) keeps it. The bottom padding clears the
// home indicator; the height cap that clears the notch is the inline FETCH_MODAL_SHEET_MAX_HEIGHT.
const SHEET_FRAME_CLASS =
  "my-0 w-full max-w-none h-[100dvh] rounded-t-3xl rounded-b-none border-x-0 border-b-0 pb-[max(1.5rem,env(safe-area-inset-bottom))]";

// A caller padding or margin class on the content wrapper means it already manages ring room
// (`px-1`, `-m-2 p-2`, ...); stacking ours on top would move its content.
const SPACING_CLASS_PATTERN = /(^|[\s:!])-?[pm][xytblr]?-/;
// A panel with no padding has nowhere for the ring room's negative margin to go.
const ZERO_PADDING_PATTERN = /(^|[\s:!])p[xy]?-0(\s|$)/;

/**
 * Whether the caller's panel `className` sets its own width. Matches the long-standing check that
 * makes `size="auto"` defer to the caller.
 * @param className - The caller's panel classes.
 * @returns True when the classes contain a width utility.
 */
export function hasCustomModalWidth(className: string): boolean {
  return className.includes("max-w-") || className.includes("w-");
}

/**
 * Resolves a size preset and height mode to panel classes plus the inline max-height cap.
 * `"auto"` and `"full"` keep their fixed-height behavior whatever `height` says.
 * @param size - The width preset.
 * @param height - `"fit"` (hug content) or `"fill"` (stretch to the cap).
 * @param className - The caller's panel classes, used to detect a custom width for `"auto"`.
 * @returns Width classes, height classes and the inline max-height value.
 */
export function getModalSizeClasses(
  size: FetchModalSize,
  height: FetchModalHeight,
  className: string
): ModalSizeClasses {
  if (size === "auto") {
    if (hasCustomModalWidth(className)) return { widthClasses: "", heightClasses: "", maxHeight: "90vh" };
    return { widthClasses: "w-[95%] md:w-[90%] lg:w-[80%]", heightClasses: "h-[90vh]", maxHeight: "90vh" };
  }
  if (size === "full") return { widthClasses: "w-screen", heightClasses: "h-screen", maxHeight: "100vh" };

  const preset = PRESET_SIZES[size] ?? PRESET_SIZES.md;
  const heightClasses = height === "fill" ? `h-full ${preset.maxHeightClass}` : preset.maxHeightClass;
  return { widthClasses: preset.widthClasses, heightClasses, maxHeight: preset.maxHeight };
}

/**
 * Builds the dialog panel's class list.
 * @param options - `size`, the resolved size classes, whether the panel renders as the mobile
 *   sheet, and the caller's `className` (applied last, so it wins).
 * @returns The merged class string.
 */
export function getModalPanelClassName(options: {
  size: FetchModalSize;
  sizeClasses: ModalSizeClasses;
  isSheet: boolean;
  className: string;
}): string {
  const { size, sizeClasses, isSheet, className } = options;
  const isFullScreen = size === "full";
  // A transparent panel is a caller drawing its own card inside; a frame would outline nothing.
  const hasSurface = !isFullScreen && !className.includes("bg-transparent");

  return cn(
    "bg-white p-6 flex flex-col",
    isFullScreen ? "rounded-none m-0" : PANEL_FRAME_CLASS,
    hasSurface && PANEL_SURFACE_CLASS,
    sizeClasses.widthClasses,
    sizeClasses.heightClasses,
    isSheet && SHEET_FRAME_CLASS,
    className
  );
}

/**
 * Builds the content wrapper's class list. The wrapper is the modal's scroller (the header and
 * bottom bar stay fixed), and it carries symmetric ring room so focus rings are not clipped.
 * @param contentClassName - The caller's wrapper classes, applied last so overrides such as
 *   `!overflow-visible` keep working.
 * @param className - The caller's panel classes; a zero-padding panel gets no ring room.
 * @returns The merged class string.
 */
export function getModalContentClassName(contentClassName: string, className: string): string {
  const hasRingRoom = !SPACING_CLASS_PATTERN.test(contentClassName) && !ZERO_PADDING_PATTERN.test(className);
  return cn(
    "flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden",
    hasRingRoom && FETCH_MODAL_RING_ROOM_CLASS,
    contentClassName
  );
}
